"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, ArrowRight, Building2, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface WelcomeStepProps {
  onNext: () => void;
  onBack?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

export function WelcomeStep({ onNext, isFirst }: WelcomeStepProps) {
  const { data: session } = useSession();
  const { toast } = useToast();
  const userName = session?.user?.name?.split(" ")[0] || "there";
  
  const [organizationName, setOrganizationName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleNext = async () => {
    if (!showForm) {
      setShowForm(true);
      return;
    }

    if (!organizationName.trim()) {
      toast({
        title: "Business name required",
        description: "Please enter your business name to continue.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/organization", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          name: organizationName.trim()
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update organization name");
      }

      toast({
        title: "Perfect!",
        description: `Welcome to JobWright, ${organizationName}!`,
      });
      
      onNext();
    } catch (error) {
      console.error("Error updating organization:", error);
      toast({
        title: "Error",
        description: "Failed to save business name. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-8 py-8">
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-600">
        {showForm ? (
          <Building2 className="h-12 w-12 text-white" />
        ) : (
          <Sparkles className="h-12 w-12 text-white" />
        )}
      </div>

      <div className="space-y-4 text-center max-w-2xl">
        <h1 className="text-4xl font-bold tracking-tight">
          {showForm ? "Let's set up your business" : `Welcome to JobWright, ${userName}!`}
        </h1>
        <p className="text-xl text-muted-foreground">
          {showForm 
            ? "What should we call your business in JobWright?"
            : "Your all-in-one platform for managing your trades business"
          }
        </p>
      </div>

      <Card className="max-w-2xl w-full">
        <CardContent className="pt-6 space-y-4">
          {!showForm ? (
            <>
              <div className="space-y-3">
                <p className="text-lg text-center">
                  JobWright helps you streamline your entire workflow - from quotes and scheduling
                  to invoicing and client communication.
                </p>
                <p className="text-center text-muted-foreground">
                  Let's get your business set up and show you around.
                  This will only take a minute!
                </p>
              </div>

              <div className="bg-info/10 dark:bg-blue-950 rounded-lg p-4 space-y-2">
                <h3 className="font-semibold text-sm">What you'll set up:</h3>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>✓ Your business information</li>
                  <li>✓ Key features for your workflow</li>
                  <li>✓ Quick tour of your new dashboard</li>
                </ul>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  placeholder="e.g., Smith Plumbing, ABC Electric, Metro HVAC"
                  disabled={isLoading}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && organizationName.trim()) {
                      handleNext();
                    }
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  This will appear on your invoices and throughout JobWright
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        onClick={handleNext}
        size="lg"
        className="gap-2 px-8"
        disabled={isLoading || (showForm && !organizationName.trim())}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            {showForm ? "Continue" : "Let's Get Started"}
            <ArrowRight className="h-5 w-5" />
          </>
        )}
      </Button>

      {isFirst && !showForm && (
        <button
          onClick={onNext}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip this tour
        </button>
      )}
    </div>
  );
}
